// `GET /docs` — the document list behind Figma frame 14 (33:1588).
// Server implementation is Wave 1's `backend/api/routes/docs_list.py`.
//
// THREE STATES THIS FILE EXISTS TO KEEP APART. Collapsing any two of them is
// the bug Wave 1 corrected on the server and the screen must not reintroduce:
//
//   401                  -> signed out. NOT an empty library.
//   200 + total: 0       -> authenticated, genuinely no documents.
//   status "unavailable" -> no LLM key is configured, so extraction status
//                           cannot be determined. NOT "not_extracted", and
//                           NOT an error — the doc exists.
//
// So the fetch throws a `DocListError` carrying the real HTTP status rather
// than a bare `Error`, and `status` is surfaced per row rather than folded
// into a boolean.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { getKey } from "../../dashboard/api";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

export type DocStatus =
  | "not_extracted"
  | "extracted"
  | "published"
  | "unavailable";

export type DocSortField = "updated_at" | "term_count";
export type DocSortOrder = "asc" | "desc";

export interface DocSummary {
  doc_id: string;
  title: string | null;
  source_kind: string;
  corpus_id: string | null;
  term_count: number;
  status: DocStatus;
  pipeline_version: number | null;
  extraction_glossary_version: number | null;
  // Null before a doc is first extracted — no ingestion timestamp is stored
  // anywhere in the system. Documented in the Wave 1 handoff, not a bug.
  created_at: string | null;
  updated_at: string | null;
}

export interface DocListResponse {
  items: DocSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** Carries the HTTP status so 401 can be told from every other failure. */
export class DocListError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`GET /docs responded ${status}`);
    this.name = "DocListError";
    this.status = status;
  }
}

export interface DocListQuery {
  limit: number;
  offset: number;
  sort: DocSortField;
  order: DocSortOrder;
  /** Null means "every status", which is the absence of the param. */
  status: DocStatus | null;
}

function docsUrl(query: DocListQuery): string {
  const url = new URL(
    `${API_BASE}/docs`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  );
  url.searchParams.set("limit", String(query.limit));
  url.searchParams.set("offset", String(query.offset));
  url.searchParams.set("sort", query.sort);
  url.searchParams.set("order", query.order);
  if (query.status) url.searchParams.set("status", query.status);
  return url.toString();
}

// Mirrors `dashboard/api.ts`: the license key is sent as a header, and the
// session cookie rides along on same-origin deploys (`VITE_API_BASE=/api`)
// under fetch's default credentials mode. Either credential resolves an org
// on this route — the Wave 1 handoff verified both.
function authHeaders(): Record<string, string> {
  const key = getKey();
  return key
    ? { Accept: "application/json", "X-mAiSight-Key": key }
    : { Accept: "application/json" };
}

export async function fetchDocList(
  query: DocListQuery,
): Promise<DocListResponse> {
  const res = await fetch(docsUrl(query), { headers: authHeaders() });
  if (!res.ok) throw new DocListError(res.status);
  return (await res.json()) as DocListResponse;
}

export function useDocList(
  query: DocListQuery,
): UseQueryResult<DocListResponse, DocListError> {
  return useQuery<DocListResponse, DocListError>({
    queryKey: [
      "doc-list",
      query.limit,
      query.offset,
      query.sort,
      query.order,
      query.status,
    ],
    queryFn: () => fetchDocList(query),
    // A 401 is a settled answer, not a blip. Retrying it four times only
    // delays the signed-out screen the reader needs to see.
    retry: (count, error) => error.status !== 401 && count < 2,
  });
}

// ---------------------------------------------------------------------------
// A3 — the vocabulary work-list (PRD 16 §4). The findings themselves are not
// new; the assignment on each one is.
//
// WHY A SECOND HEALTH HOOK RATHER THAN EXTENDING `dashboard/api.ts`'s. That
// module is another agent's fence this wave, and `AnalyticsScreen` reads its
// `useVocabularyHealth` for a count. This hook fetches the same endpoint with
// the fields the work-list needs (`key`, `assignment`, `include_dismissed`)
// and leaves that one alone. `polish` should collapse the two.
//
// TWO CREDENTIALS, ONE PER VERB, AND THEY ARE NOT INTERCHANGEABLE. The GET
// takes either, so it carries the key header. The PUT records *who* assigned
// and therefore takes only the session cookie — `credentials: "same-origin"`
// and no key header, exactly as `screens/ops/api.ts` does. `same-origin` and
// not `include`: the API sets `allow_credentials=False`, so a cross-origin
// fetch carrying `include` is rejected before the response is read. The
// consequence is that assigning does not work against a cross-origin dev API
// (Vite 5173 -> API 8000) and does work deployed and under `gate-demo.sh`,
// where the SPA is same-origin. Same limitation the other session routes have.

export type AssignmentStatus = "open" | "in_progress" | "done";

export interface FindingAssignment {
  /** The customer's own employee. Null = nobody owns it yet. */
  assignee: string | null;
  status: AssignmentStatus;
  /** Naive-UTC, no offset or Z suffix. Null = not snoozed. */
  snoozed_until: string | null;
  updated_by: string;
  updated_at: string;
}

export interface WorkListFinding {
  kind: "stalls" | "inconsistent" | "unexplained";
  term: string;
  doc_ids: string[];
  detail: string;
  score: number;
  /**
   * Stable across recomputation — a finding is rebuilt from current data on
   * every request and has no id of its own. Derivation and the reasoning are
   * in `backend/corpus/assignments.py`. Never construct one client-side;
   * echo back the one the server sent.
   */
  key: string;
  assignment: FindingAssignment | null;
  /**
   * `null` = live work, ranked normally. A string = there is no work left to
   * rank, and the finding survives only because somebody is assigned to it;
   * the string is the backend's own reason (`backend/corpus/health.py`).
   *
   * Two different reasons reach here and they are OPPOSITE claims — readers
   * stopped needing a second look (a win) versus nobody visited the term at
   * all (nothing measured). The row's `detail` sentence carries that
   * distinction; a badge cannot, which is why the badge never replaces it.
   */
  unranked_reason: string | null;
  /**
   * WHICH of the two — because they are opposite claims and a badge cannot
   * read prose. `resolved` is earned: readers came and stopped needing a
   * second look. `no_traffic` proves nothing either way, and a green
   * "Resolved" pill over it would manufacture a success out of missing
   * visits. Absent on an older payload, which is treated as "cannot claim".
   */
  unranked_kind?: "resolved" | "no_traffic" | null;
}

export interface AssignInput {
  finding_key: string;
  assignee: string | null;
  status: AssignmentStatus;
  snoozed_until: string | null;
}

function healthUrl(
  orgSlug: string,
  days: number,
  includeDismissed: boolean,
): string {
  const corpus = encodeURIComponent(orgSlug);
  const dismissed = includeDismissed ? "&include_dismissed=true" : "";
  return `${API_BASE}/dashboards/${encodeURIComponent(orgSlug)}/health?days=${days}&corpus_id=${corpus}${dismissed}`;
}

export function useVocabWorkList(
  orgSlug: string,
  days: number,
  includeDismissed: boolean,
): UseQueryResult<WorkListFinding[] | null, Error> {
  return useQuery<WorkListFinding[] | null, Error>({
    queryKey: ["vocab-worklist", orgSlug, days, includeDismissed],
    queryFn: async () => {
      const url = healthUrl(orgSlug, days, includeDismissed);
      const res = await fetch(url, { headers: authHeaders() });
      // 404 = no such corpus for this org yet. An absent panel, not a failure.
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`GET ${url} failed: ${res.status}`);
      return ((await res.json()) as { findings: WorkListFinding[] }).findings;
    },
  });
}

export function useAssignFinding(orgSlug: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, AssignInput>({
    mutationFn: async (input) => {
      const url = `${API_BASE}/dashboards/${encodeURIComponent(orgSlug)}/health/assignments`;
      const res = await fetch(url, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ corpus_id: orgSlug, ...input }),
      });
      if (!res.ok) throw new Error(`PUT ${url} failed: ${res.status}`);
      return res.json();
    },
    // Every dismissal filter shares the server's state, so invalidate the
    // whole list rather than the one variant that happens to be mounted.
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["vocab-worklist", orgSlug] }),
  });
}

/** Seven days out, as naive UTC — the format the API stores and returns. */
export function snoozeUntil(days: number): string {
  const at = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return at.toISOString().replace("Z", "");
}
